'use client';

import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { MeetingHistory } from './components/MeetingHistory';
import styles from '../styles/Home.module.css';

type SessionUser = { username: string; role: string; phone?: string; id?: string };
const SessionCtx = React.createContext<{
  user: SessionUser | null;
  loading: boolean;
  setUser: React.Dispatch<React.SetStateAction<SessionUser | null>>;
}>({ user: null, loading: true, setUser: () => {} });

function SessionProvider({ children }: React.PropsWithChildren<{}>) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  React.useEffect(() => {
    fetch('/api/auth/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.ok && d.user) setUser(d.user);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return (
    <SessionCtx.Provider value={{ user, loading, setUser }}>{children}</SessionCtx.Provider>
  );
}
function useSession() {
  return React.useContext(SessionCtx);
}

// ===== 与登录页完全一致的视觉常量（login/page.tsx 同款）=====
const loginInputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '15px 16px',
  fontSize: '17px',
  lineHeight: '1.4',
  borderRadius: 12,
  border: '1px solid #d3dae5',
  background: '#f8fafc',
  color: '#101828',
  outline: 'none',
};
const loginBtnBase: React.CSSProperties = {
  boxSizing: 'border-box',
  fontSize: '1.02rem',
  borderRadius: 12,
  background: '#1f6feb',
  border: 'none',
  color: '#fff',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: '1.4',
  paddingBlock: '0.9rem',
};
const loginBtnDisabled: React.CSSProperties = {
  ...loginBtnBase,
  background: '#a9c6f2',
  cursor: 'not-allowed',
};

function DemoMeetingTab(props: { label: string }) {
  const router = useRouter();
  const { user } = useSession();
  const [roomName, setRoomName] = useState('');
  // 可选：会议开始时间（预约会议必填；仅用于分享文案展示，不写入链接）
  const [meetTime, setMeetTime] = useState('');
  // 预约成功反馈（已复制预约信息）
  const [appointCopied, setAppointCopied] = useState(false);
  const [copied, setCopied] = useState(false);
  // 当前会议名/时间/摄像头设置下是否已复制过链接（copied 2 秒后自动消失，此为持久标记；
  // 用于「进入会议前未复制 → 弹窗提醒一键复制」）
  const [linkCopied, setLinkCopied] = useState(false);
  // 点「进入会议」但尚未复制链接时的提醒弹窗
  const [showCopyPrompt, setShowCopyPrompt] = useState(false);
  // 会议默认关闭摄像头（默认勾选）：勾掉后，进会者进入时在预览页可自行开启摄像头（链接带 cam=on）
  const [camOff, setCamOff] = useState(true);

  const requireLogin = (path: string): boolean => {
    if (user) return true;
    router.push(`/login?next=${encodeURIComponent(path)}`);
    return false;
  };

  // 创建者勾掉"会议默认关闭摄像头" → 本会议启用摄像头：链接加 ?cam=on
  const camQuery = !camOff ? '?cam=on' : '';

  const joinNamedRoom = () => {
    const name = roomName.trim();
    if (!name) return;
    // 进会前提醒：本次输入还没复制过链接 → 弹窗，让用户复制后即可进入（避免主持人忘发链接参会者进不来）
    if (!linkCopied) {
      setShowCopyPrompt(true);
      return;
    }
    goToRoom(name);
  };

  // 组装固定会议室链接并进入（用户 ID 前缀：不同用户输入相同名称 = 各自独立的会议室，互不干扰）
  const goToRoom = (name: string) => {
    const ownerKey = user?.id || '';
    const fullName = ownerKey ? `${ownerKey}-${name}` : name;
    const room = `/rooms/${encodeURIComponent(fullName)}${camQuery}`;
    if (!requireLogin(room)) return;
    router.push(room);
  };

  const trimmed = roomName.trim();
  const loggedIn = !!user;
  // 与进入动作一致：分享链接也带用户 ID 前缀（如 /rooms/u_1f98cb-测试2）
  const ownerKey = loggedIn && user ? user.id || '' : '';
  const fullDisplayName = ownerKey && trimmed ? `${ownerKey}-${trimmed}` : '';
  const shareRawUrl =
    typeof window !== 'undefined' && fullDisplayName
      ? `${window.location.origin}/rooms/${encodeURIComponent(fullDisplayName)}${camQuery}`
      : '';

  // 开始时间展示文本：2026-09-08T10:00 → 2026/9/8 10:00
  const fmtMeetingTime = (v: string): string => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  // 分享文案：周会 2026/9/7 13:00 会议链接：https://…（名称=会议主题；时间选填）
  const shareText =
    shareRawUrl && trimmed
      ? `${trimmed}${meetTime ? ` ${fmtMeetingTime(meetTime)}` : ''} 会议链接：${shareRawUrl}`
      : '';

  const copyLink = async () => {
    if (!shareText) return;
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setLinkCopied(true);
    } catch {
      // 兼容非安全上下文：兜底用临时 textarea
      try {
        const ta = document.createElement('textarea');
        ta.value = shareText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setLinkCopied(true);
      } catch {
        return;
      }
    }
    setTimeout(() => setCopied(false), 2000);
  };

  // 预约会议：会议名 + 开始时间（必填）→ 保存到"我的会议记录"并复制完整会议信息文案，不直接进入会议
  const makeAppointment = async () => {
    if (!trimmed) {
      alert('请先输入会议名。');
      return;
    }
    if (!meetTime) {
      alert('预约会议需要选择会议开始时间。');
      return;
    }
    if (!shareText || !fullDisplayName) {
      alert('生成失败，请重试。');
      return;
    }
    try {
      const res = await fetch('/api/meetings/appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomName: fullDisplayName,
          startTime: meetTime,
          camOn: !camOff, // 记录该预约是否启用摄像头，方便"我的会议记录"里复制链接/进入会议保持一致
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        alert(data?.error || '预约保存失败，请稍后重试');
        return;
      }
    } catch {
      alert('网络异常，请稍后重试');
      return;
    }
    void copyLink();
    setAppointCopied(true);
    setTimeout(() => setAppointCopied(false), 2000);
    // 通知"我的会议记录"刷新，让刚预约的会议立刻出现
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('meeting-history-refresh'));
    }
  };

  return (
    <div className={styles.tabContent}>
      {/* 品牌 logo：放大，与输入框左对齐 */}
      <img
        src="/images/ruobilin.svg"
        alt="RuoBilin"
        style={{ display: 'block', width: 'min(300px, 100%)', height: 'auto' }}
      />
      <div
        className="header"
        style={{ textAlign: 'center', padding: 0 }}
      >
          <h2
            style={{
              marginBlock: '0 0.2rem',
              fontSize: '1.05rem',
              fontWeight: 600,
              color: '#1f2937',
              letterSpacing: '0.04em',
            }}
          >
            海内存知己，天涯若比邻
          </h2>
        </div>
      {loggedIn ? (
        <p style={{ margin: '0 auto', textAlign: 'center', color: '#7a8699', fontSize: '0.9rem' }}>
          输入会议名，创建或加入固定会议室
        </p>
      ) : (
        <p style={{ margin: '0 auto', textAlign: 'center', color: '#7a8699', fontSize: '0.9rem' }}>
          登录后即可发起会议；或直接用主持人分享的链接加入
        </p>
      )}
      <div
        className="rb-create-row"
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: '0.8rem',
          marginTop: '0.15rem',
        }}
      >
        <input
          style={
            loggedIn
              ? { ...loginInputStyle, flex: '1 1 0', minWidth: 0 }
              : {
                  ...loginInputStyle,
                  flex: '1 1 0',
                  minWidth: 0,
                  opacity: 0.72,
                  cursor: 'not-allowed',
                }
          }
          placeholder={loggedIn ? '会议名，例如：周会' : '请先登录后再创建会议'}
          value={roomName}
          disabled={!loggedIn}
          onChange={(ev) => {
            setRoomName(ev.target.value);
            setCopied(false);
            setLinkCopied(false);
            setAppointCopied(false);
          }}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') joinNamedRoom();
          }}
        />
        <button
          className="lk-button rb-enter-btn"
          onClick={joinNamedRoom}
          disabled={!loggedIn}
          style={
            loggedIn
              ? { ...loginBtnBase, width: 120, flexShrink: 0, paddingInline: '0.5rem' }
              : { ...loginBtnDisabled, width: 120, flexShrink: 0, paddingInline: '0.5rem' }
          }
        >
          进入会议
        </button>
      </div>

      {loggedIn ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              fontSize: '0.9rem',
              color: 'var(--lk-fg, #101828)',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={camOff}
              onChange={(ev) => {
                setCamOff(ev.target.checked);
                setCopied(false);
                setLinkCopied(false);
              }}
              style={{ width: 16, height: 16, accentColor: '#1f6feb', cursor: 'pointer' }}
            />
            会议默认关闭摄像头
          </label>
          {!camOff && (
            <div style={{ fontSize: '0.78rem', color: '#7a8699', marginLeft: '1.45rem' }}>
              已启用摄像头：每个人进会时都可自行选择是否开启，分享链接将带视频标记
            </div>
          )}
          <label
            htmlFor="meet-time-picker"
            style={{ fontSize: '0.8rem', color: '#7a8699', marginLeft: '0.1rem' }}
          >
            会议开始时间（预约会议必填）
          </label>
          <input
            id="meet-time-picker"
            type="datetime-local"
            value={meetTime}
            onChange={(ev) => {
              setMeetTime(ev.target.value);
              setCopied(false);
              setLinkCopied(false);
              setAppointCopied(false);
            }}
            style={{ ...loginInputStyle, width: '100%' }}
          />
          <button
            type="button"
            className="lk-button"
            onClick={makeAppointment}
            style={
              appointCopied
                ? { ...loginBtnBase, width: '100%', background: '#16a34a', borderColor: '#16a34a' }
                : { ...loginBtnBase, width: '100%' }
            }
          >
            {appointCopied ? '已保存并复制预约信息 ✓' : '预约会议'}
          </button>
        </div>
      ) : null}

      {trimmed && loggedIn ? (
        <div
          style={{
            marginTop: '0.8rem',
            padding: '0.7rem 0.9rem',
            background: 'rgba(255,255,255,0.85)',
            border: '1px solid var(--lk-border-color, rgba(16,24,40,0.15))',
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.45rem',
          }}
        >
          <div style={{ fontSize: '0.82rem', color: 'var(--lk-fg4, #64748b)' }}>
            会议信息（复制发给参会者，打开即可加入）：
          </div>
          <div
            className="rb-share-row"
            style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '0.9rem',
                color: 'var(--lk-fg, #101828)',
                wordBreak: 'break-all',
                whiteSpace: 'normal',
                lineHeight: 1.5,
              }}
              title={shareText}
            >
              {shareText}
            </span>
            <button
              type="button"
              onClick={copyLink}
              style={{
                flexShrink: 0,
                fontSize: '0.88rem',
                padding: '0.5rem 0.9rem',
                borderRadius: 12,
                cursor: 'pointer',
                background: copied ? '#16a34a' : '#1f6feb',
                border: 'none',
                color: '#fff',
              }}
            >
              {copied ? '已复制 ✓' : '复制链接'}
            </button>
          </div>
        </div>
      ) : null}

      {/* 进会前提醒：本次输入还没复制过链接 → 弹窗内可直接复制，复制后点「进入会议」即进会 */}
      {showCopyPrompt ? (
        <div
          onClick={() => setShowCopyPrompt(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.4)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 100,
            padding: '1rem',
            boxSizing: 'border-box',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rb-panel-card"
            style={{
              width: '100%',
              maxWidth: 380,
              background: '#fff',
              borderRadius: 18,
              boxShadow: '0 18px 50px rgba(15,23,42,0.2)',
              boxSizing: 'border-box',
              padding: '1.8rem 1.6rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.9rem',
            }}
          >
            <h3 style={{ margin: 0, color: '#101828', fontSize: '1.15rem', fontWeight: 600 }}>
              先复制链接，再进入会议
            </h3>
            <p style={{ margin: 0, color: '#7a8699', fontSize: '0.9rem', lineHeight: 1.6 }}>
              把下面的会议信息发给参会者，他们打开链接即可加入；复制好后点「进入会议」。
            </p>
            <div
              style={{
                fontSize: '0.88rem',
                color: 'var(--lk-fg, #101828)',
                lineHeight: 1.6,
                wordBreak: 'break-all',
                whiteSpace: 'normal',
                background: '#f8fafc',
                border: '1px solid #d3dae5',
                borderRadius: 10,
                padding: '0.6rem 0.8rem',
                maxHeight: 150,
                overflowY: 'auto',
              }}
            >
              {shareText}
            </div>
            <div style={{ display: 'flex', gap: '0.7rem', marginTop: '0.3rem' }}>
              <button
                type="button"
                onClick={copyLink}
                style={{
                  ...loginBtnBase,
                  flex: 1,
                  background: copied ? '#16a34a' : '#ffffff',
                  color: copied ? '#fff' : '#1f6feb',
                  border: copied ? 'none' : '1px solid #1f6feb',
                  fontSize: '0.95rem',
                }}
              >
                {copied ? '已复制 ✓' : '复制链接'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCopyPrompt(false);
                  goToRoom(roomName.trim());
                }}
                style={{ ...loginBtnBase, flex: 1.15, fontSize: '0.95rem' }}
              >
                进入会议
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SessionBar() {
  const router = useRouter();
  const { user, setUser } = useSession();
  const [showPwd, setShowPwd] = useState(false);

  if (!user) {
    return (
      <div className="rb-topbar" style={{ position: 'fixed', top: 14, right: 18, zIndex: 50 }}>
        <button
          className="lk-button"
          style={{ fontSize: '0.85rem', padding: '0.2rem 0.8rem' }}
          onClick={() => router.push('/login')}
        >
          登录
        </button>
      </div>
    );
  }
  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch {
      /* ignore */
    }
    setUser(null);
    // 整页刷新：彻底清掉各组件缓存的登录态与会议记录数据
    window.location.assign('/');
  };
  return (
    <>
      <div
        className="rb-topbar"
        style={{
          position: 'fixed',
          top: 14,
          right: 18,
          zIndex: 50,
          display: 'flex',
          gap: '0.7rem',
          alignItems: 'center',
          fontSize: '0.85rem',
          color: 'var(--lk-fg, #101828)',
        }}
      >
        {user.role === 'admin' ? (
          <a href="/admin" style={{ color: '#1f6feb', textDecoration: 'none' }}>
            用户管理
          </a>
        ) : null}
        <span className="rb-username">{user.username}</span>
        <button
          className="lk-button"
          style={{ fontSize: '0.8rem', padding: '0.15rem 0.65rem' }}
          onClick={() => setShowPwd(true)}
        >
          修改密码
        </button>
        <button
          className="lk-button"
          style={{ fontSize: '0.8rem', padding: '0.15rem 0.65rem' }}
          onClick={logout}
        >
          退出
        </button>
      </div>
      {showPwd ? <ChangePasswordDialog onClose={() => setShowPwd(false)} /> : null}
    </>
  );
}

// 修改密码弹窗（登录页同款视觉）
function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError('');
    if (!oldPassword) {
      setError('请输入当前密码');
      return;
    }
    if (newPassword.length < 6) {
      setError('新密码至少 6 位');
      return;
    }
    if (newPassword !== confirm) {
      setError('两次输入的新密码不一致');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldPassword, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.error || '修改失败，请重试');
        setBusy(false);
        return;
      }
      alert('密码修改成功，下次登录请使用新密码');
      onClose();
    } catch {
      setError('网络异常，请重试');
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: '1rem',
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 380,
          background: '#fff',
          borderRadius: 18,
          boxShadow: '0 18px 50px rgba(15,23,42,0.2)',
          boxSizing: 'border-box',
          padding: '1.8rem 1.6rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.9rem',
        }}
      >
        <h3 style={{ margin: 0, color: '#101828', fontSize: '1.15rem', fontWeight: 600 }}>
          修改密码
        </h3>
        <input
          type="password"
          placeholder="当前密码"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          style={loginInputStyle}
          autoComplete="current-password"
        />
        <input
          type="password"
          placeholder="新密码（至少 6 位）"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          style={loginInputStyle}
          autoComplete="new-password"
        />
        <input
          type="password"
          placeholder="确认新密码"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          style={loginInputStyle}
          autoComplete="new-password"
        />
        {error ? (
          <div style={{ color: '#e5484d', fontSize: '0.85rem' }}>{error}</div>
        ) : null}
        <div style={{ display: 'flex', gap: '0.7rem', marginTop: '0.3rem' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              ...loginBtnBase,
              flex: 1,
              background: '#eef1f5',
              color: '#5b6472',
              paddingBlock: '0.75rem',
              fontSize: '0.95rem',
            }}
          >
            取消
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            style={busy ? { ...loginBtnDisabled, flex: 1, fontSize: '0.95rem' } : { ...loginBtnBase, flex: 1, fontSize: '0.95rem' }}
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
}

function HomeBody() {
  const { user } = useSession();
  return (
    <div
      className="rb-home-wrap"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <SessionBar />
      <main className={styles.main} data-lk-theme="default">
        <DemoMeetingTab label="快速开会" />
        {user ? <MeetingHistory /> : null}
      </main>
      <footer
        style={{
          textAlign: 'center',
          padding: '0.6rem 1rem 1.1rem',
          fontSize: '0.78rem',
          color: '#8a94a6',
        }}
      >
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noreferrer"
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          鲁ICP备2026051761号-1
        </a>
      </footer>
    </div>
  );
}

export default function Page() {
  return (
    <SessionProvider>
      <HomeBody />
    </SessionProvider>
  );
}
